<template>
  <div class="gm-page tabletop-map-editor">
    <!-- Top Toolbar -->
    <div class="gm-topbar">
      <div class="toolbar-section">
        <div>
          <div class="gm-eyebrow">Game Master</div>
          <h1 class="gm-title">Tabletop Map Editor</h1>
        </div>
        <span v-if="currentMap?.metadata?.name" class="map-name">{{ currentMap.metadata.name }}</span>
      </div>

      <div class="gm-topbar__actions">
        <button class="gm-btn" @click="showLibrary = true">
          Library
        </button>
        <button class="gm-btn" @click="showNewMapDialog = true">
          New
        </button>
        <button class="gm-btn" @click="importFromDisk">
          Import
        </button>
        <button class="gm-btn gm-btn--primary" @click="saveToBackend" :disabled="!currentMap || isSaving">
          {{ isSaving ? 'Saving...' : 'Save' }}
        </button>
        <button class="gm-btn" @click="exportToDisk" :disabled="!currentMap || !currentMap._templateId">
          Export
        </button>
        <button class="gm-btn" @click="importBackground" :disabled="!currentMap">
          Background
        </button>
        <button class="gm-btn" @click="handleAutoMap" :disabled="!currentMap">
          Auto-map
        </button>
        <button
            class="gm-btn"
            :class="{ 'is-active': show3dPane }"
            :disabled="!currentMap"
            data-testid="toggle-3d-pane"
            @click="toggle3dPane"
        >
          {{ $t('battleMap3d.editor.toggle') }}
        </button>
        <button class="gm-btn" @click="testMap" :disabled="!currentMap || !currentMap._templateId">
          Test
        </button>
        <button
            v-if="campaignBuilderReturn"
            type="button"
            class="gm-btn gm-btn--primary"
            :disabled="!canReturnToCampaignBuilder || isReturningToCampaignBuilder"
            @click="returnToCampaignBuilder"
        >
          <i class="fas fa-arrow-rotate-left" aria-hidden="true"></i>
          {{ isReturningToCampaignBuilder ? 'Returning…' : 'Use this map in the Campaign Builder' }}
        </button>
      </div>
    </div>

    <p v-if="campaignBuilderReturn" class="gm-notice campaign-builder-return-notice">
      <i class="fas fa-diagram-project" aria-hidden="true"></i>
      Save this map, then use "Use this map in the Campaign Builder" to assign it to the position and
      return to the pipeline.
    </p>

    <p v-if="campaignBuilderReturnError" class="gm-issue gm-issue--error campaign-builder-return-error" role="alert">
      <i class="fas fa-triangle-exclamation" aria-hidden="true"></i>
      {{ campaignBuilderReturnError }}
    </p>

    <!-- Main Content Area -->
    <div class="gm-page__body editor-content">
      <!-- Left Panel -->
      <div class="left-panel gm-scroll">
        <ToolPanel />
        <LayerSelector />
      </div>

      <!-- Center Panel - Canvas -->
      <div class="center-panel gm-panel gm-panel--flush" :class="{ 'center-panel--split': show3dPane && currentMap }">
        <div v-if="!currentMap" class="gm-empty">
          <h2 class="gm-empty__title">No Map Loaded</h2>
          <p class="gm-empty__hint">Create a new map or import an existing one to get started</p>
          <button class="gm-btn gm-btn--primary" @click="showNewMapDialog = true">
            Create New Map
          </button>
        </div>
        <MapCanvas v-else :key="currentMap?.metadata?.created || 'default'" ref="mapCanvas" />
        <Editor3DPane
          v-if="show3dPane && currentMap"
          class="center-panel__pane"
          :map-data="currentMap"
          @paint="apply3dPaint"
        />
      </div>

      <!-- Right Panel -->
      <div class="right-panel gm-scroll">
        <!-- In normal editing modes, show cell Properties; in Map Config mode, show Grid Configuration -->
        <PropertiesPanel v-if="editorMode !== 'config'" />
        <GridConfigPanel v-else />
      </div>
    </div>

    <!-- New Map Dialog -->
    <div v-if="showNewMapDialog" class="modal-overlay" @click.self="showNewMapDialog = false">
      <div class="modal-dialog gm-panel gm-panel--raised">
        <h2 class="gm-title modal-title">Create New Map</h2>
        <div class="modal-content">
          <div class="gm-field">
            <label class="gm-label">Map Name</label>
            <input
              v-model="newMapName"
              type="text"
              class="gm-input"
              placeholder="Enter map name"
              @keyup.enter="handleCreateNewMap"
            />
          </div>
          <div class="gm-field">
            <label class="gm-label">Author</label>
            <input
              v-model="newMapAuthor"
              type="text"
              class="gm-input"
              placeholder="Your name"
              @keyup.enter="handleCreateNewMap"
            />
          </div>
        </div>
        <div class="modal-actions">
          <button class="gm-btn gm-btn--ghost" @click="showNewMapDialog = false">
            Cancel
          </button>
          <button class="gm-btn gm-btn--primary" @click="handleCreateNewMap">
            Create
          </button>
        </div>
      </div>
    </div>

    <!-- Battle Map Library Modal -->
    <BattleMapLibrary
      :visible="showLibrary"
      @close="showLibrary = false"
      @load-template="handleLoadFromLibrary"
    />

    <!-- Hidden file inputs -->
    <input
      ref="importInput"
      type="file"
      accept=".png,image/png"
      style="display: none"
      @change="handleImportFile"
    />
    <input
      ref="backgroundInput"
      type="file"
      accept="image/*"
      style="display: none"
      @change="handleBackgroundFile"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { defineAsyncComponent } from 'vue';
import MapCanvas from '@/components/TabletopEditor/MapCanvas.vue';
import LayerSelector from '@/components/TabletopEditor/LayerSelector.vue';
import ToolPanel from '@/components/TabletopEditor/ToolPanel.vue';
import PropertiesPanel from '@/components/TabletopEditor/PropertiesPanel.vue';
import GridConfigPanel from '@/components/TabletopEditor/GridConfigPanel.vue';
import BattleMapLibrary from '@/components/BattleMapLibrary/BattleMapLibrary.vue';
import battleMapService from '@/services/battleMapService';
import campaignBuilderService from '@/services/campaignBuilderService.js';
import { extractMetadataFromPNG } from '@/utils/pngMetadata.js';

const CAMPAIGN_BUILDER_RETURN_KEY = 'dx.campaignBuilder.tacticalMapReturn';

// ----- Movement edge map helpers -----
const CARDINAL_DELTAS = {
  north: { dx: 0, dy: -1 },
  south: { dx: 0, dy: 1 },
  east: { dx: 1, dy: 0 },
  west: { dx: -1, dy: 0 }
};

const OPPOSITE_DIRECTION = {
  north: 'south',
  south: 'north',
  east: 'west',
  west: 'east'
};

const DIAGONAL_DELTAS = {
  northEast: { dx: 1, dy: -1 },
  northWest: { dx: -1, dy: -1 },
  southEast: { dx: 1, dy: 1 },
  southWest: { dx: -1, dy: 1 }
};

const OPPOSITE_DIAGONAL_DIRECTION = {
  northEast: 'southWest',
  southWest: 'northEast',
  northWest: 'southEast',
  southEast: 'northWest'
};

function buildCellLookup(cells) {
  const lookup = new Map();
  if (Array.isArray(cells)) {
    cells.forEach(cell => {
      lookup.set(`${cell.x},${cell.y},${cell.layer}`, cell);
    });
  }
  return lookup;
}

function getCellState(mapData, lookup, x, y, layerId) {
  if (!mapData || !mapData.grid) {
    return {
      available: false,
      connections: {
        north: false,
        south: false,
        east: false,
        west: false,
        northEast: false,
        northWest: false,
        southEast: false,
        southWest: false
      }
    };
  }

  const { columns, rows } = mapData.grid;

  // Out of bounds -> treated as unavailable
  if (x < 0 || y < 0 || x >= columns || y >= rows) {
    return {
      available: false,
      connections: {
        north: false,
        south: false,
        east: false,
        west: false,
        northEast: false,
        northWest: false,
        southEast: false,
        southWest: false
      }
    };
  }

  const layerMeta = Array.isArray(mapData.layers)
    ? mapData.layers.find(l => l.id === layerId)
    : null;

  if (!layerMeta || layerMeta.active === false) {
    return {
      available: false,
      connections: {
        north: false,
        south: false,
        east: false,
        west: false,
        northEast: false,
        northWest: false,
        southEast: false,
        southWest: false
      }
    };
  }

  const key = `${x},${y},${layerId}`;
  const cell = lookup.get(key);

  if (!cell) {
    // No explicit cell data -> treat as a default available cell with all connections open
    return {
      available: true,
      connections: {
        north: true,
        south: true,
        east: true,
        west: true,
        // Diagonals default to open unless explicitly blocked
        northEast: undefined,
        northWest: undefined,
        southEast: undefined,
        southWest: undefined
      }
    };
  }

  const connections = cell.connections || {};

  return {
    available: cell.available !== false,
    connections: {
      north: connections.north !== false,
      south: connections.south !== false,
      east: connections.east !== false,
      west: connections.west !== false,
      // Diagonal flags are interpreted as: false => blocked, anything else => open
      northEast: connections.northEast,
      northWest: connections.northWest,
      southEast: connections.southEast,
      southWest: connections.southWest
    }
  };
}

function canCardinalMove(mapData, lookup, x, y, layerId, direction) {
  const deltas = CARDINAL_DELTAS[direction];
  if (!deltas) return false;

  const fromState = getCellState(mapData, lookup, x, y, layerId);
  if (!fromState.available) return false;

  const targetX = x + deltas.dx;
  const targetY = y + deltas.dy;
  const toState = getCellState(mapData, lookup, targetX, targetY, layerId);

  if (!toState.available) return false;

  const opposite = OPPOSITE_DIRECTION[direction];
  return !!fromState.connections[direction] && !!toState.connections[opposite];
}

function canDiagonalMove(mapData, lookup, x, y, layerId, direction) {
  const deltas = DIAGONAL_DELTAS[direction];
  if (!deltas) return false;

  const fromState = getCellState(mapData, lookup, x, y, layerId);
  if (!fromState.available) return false;

  const targetX = x + deltas.dx;
  const targetY = y + deltas.dy;
  const toState = getCellState(mapData, lookup, targetX, targetY, layerId);
  if (!toState.available) return false;

  // Enforce no corner cutting: diagonal move only if both related
  // cardinal moves are possible from the source cell.
  const requires = {
    northEast: ['north', 'east'],
    northWest: ['north', 'west'],
    southEast: ['south', 'east'],
    southWest: ['south', 'west']
  };

  const deps = requires[direction] || [];
  if (deps.length === 2) {
    if (
      !canCardinalMove(mapData, lookup, x, y, layerId, deps[0]) ||
      !canCardinalMove(mapData, lookup, x, y, layerId, deps[1])
    ) {
      return false;
    }
  }

  // Diagonal flags: if either side explicitly blocks this diagonal,
  // the move is not allowed. When undefined, it is treated as open.
  const fromDiag = fromState.connections && fromState.connections[direction];
  const oppositeDiag = OPPOSITE_DIAGONAL_DIRECTION[direction];
  const toDiag = oppositeDiag && toState.connections
    ? toState.connections[oppositeDiag]
    : undefined;

  if (fromDiag === false || toDiag === false) {
    return false;
  }

  return true;
}

function buildMovementEdges(mapData) {
  if (!mapData || !mapData.grid || !Array.isArray(mapData.layers)) {
    return [];
  }

  const { columns, rows } = mapData.grid;
  const lookup = buildCellLookup(mapData.cells || []);
  const edges = [];

  mapData.layers.forEach(layerMeta => {
    if (!layerMeta || layerMeta.active === false) return;

    const layerId = layerMeta.id;

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < columns; x++) {
        const fromState = getCellState(mapData, lookup, x, y, layerId);
        if (!fromState.available) continue;

        // Cardinal edges
        Object.keys(CARDINAL_DELTAS).forEach(direction => {
          if (canCardinalMove(mapData, lookup, x, y, layerId, direction)) {
            const { dx, dy } = CARDINAL_DELTAS[direction];
            edges.push({
              from: { x, y, layer: layerId },
              to: { x: x + dx, y: y + dy, layer: layerId },
              direction
            });
          }
        });

        // Diagonal edges derived from cardinal movement
        const diagonals = [
          { direction: 'northEast', dx: 1, dy: -1 },
          { direction: 'northWest', dx: -1, dy: -1 },
          { direction: 'southEast', dx: 1, dy: 1 },
          { direction: 'southWest', dx: -1, dy: 1 }
        ];

        diagonals.forEach(({ direction, dx, dy }) => {
          if (canDiagonalMove(mapData, lookup, x, y, layerId, direction)) {
            const targetState = getCellState(mapData, lookup, x + dx, y + dy, layerId);
            if (!targetState.available) return;

            edges.push({
              from: { x, y, layer: layerId },
              to: { x: x + dx, y: y + dy, layer: layerId },
              direction
            });
          }
        });
      }
    }
  });

  return edges;
}

export default {
  name: 'TabletopMapEditor',
  components: {
    MapCanvas,
    LayerSelector,
    ToolPanel,
    PropertiesPanel,
    GridConfigPanel,
    BattleMapLibrary,
    Editor3DPane: defineAsyncComponent(() => import('@/components/TabletopEditor/Editor3DPane.vue'))
  },
  data() {
    return {
      showNewMapDialog: false,
      showLibrary: false,
      show3dPane: false,
      newMapName: '',
      newMapAuthor: '',
      isSaving: false,
      campaignBuilderReturn: null,
      isReturningToCampaignBuilder: false,
      campaignBuilderReturnError: null
    };
  },
  computed: {
    ...mapGetters('tabletopEditor', [
      'currentMap',
      'isDirty',
      'editorMode',
      'gridVisible',
      'backgroundVisible'
    ]),
    canReturnToCampaignBuilder() {
      return Boolean(this.campaignBuilderReturn && this.currentMap?._templateId);
    }
  },
  created() {
    if (this.$route?.query?.campaignBuilderReturn !== '1') return;
    try {
      const raw = sessionStorage.getItem(CAMPAIGN_BUILDER_RETURN_KEY);
      this.campaignBuilderReturn = raw ? JSON.parse(raw) : null;
    } catch {
      this.campaignBuilderReturn = null;
    }
  },
  methods: {
    ...mapActions('tabletopEditor', [
      'createNewMap',
      'loadMap',
      'saveMap',
      'setBackgroundImage',
      'updateGridConfig',
      'autoGenerateEdges'
    ]),

    toggle3dPane() {
      this.show3dPane = !this.show3dPane;
    },

    apply3dPaint(paintedMap) {
      const gridVisible = this.gridVisible;
      const backgroundVisible = this.backgroundVisible;
      this.loadMap(paintedMap);
      this.$store.commit('tabletopEditor/SET_GRID_VISIBLE', gridVisible);
      this.$store.commit('tabletopEditor/SET_BACKGROUND_VISIBLE', backgroundVisible);
      this.$store.commit('tabletopEditor/SET_DIRTY', true);
    },

    handleCreateNewMap() {
      if (!this.newMapName.trim()) {
        return;
      }

      this.createNewMap({
        name: this.newMapName.trim(),
        author: this.newMapAuthor.trim() || 'Unknown'
      });

      this.showNewMapDialog = false;
      this.newMapName = '';
      this.newMapAuthor = '';
    },

    // Import PNG from disk via backend
    importFromDisk() {
      this.$refs.importInput.click();
    },

    async handleImportFile(event) {
      const file = event.target.files[0];
      if (!file) return;

      console.log('[TabletopEditor] Importing file via backend:', file.name);

      try {
        // Import through backend service
        const mapData = await battleMapService.importFromDisk(file);

        // Load map in editor
        this.loadMap(mapData);

        // Set background image from loaded data
        if (mapData.backgroundImage) {
          this.setBackgroundImage(mapData.backgroundImage);
        }

        // Set reference dimensions
        if (mapData.grid?.referenceImageWidth && mapData.grid?.referenceImageHeight) {
          this.updateGridConfig({
            referenceImageWidth: mapData.grid.referenceImageWidth,
            referenceImageHeight: mapData.grid.referenceImageHeight
          });
        }

        console.log('[TabletopEditor] ✅ Map imported successfully:', mapData.metadata?.name, 'Template ID:', mapData._templateId);
      } catch (error) {
        console.error('[TabletopEditor] ❌ Import error:', error.message);
      }

      // Reset input
      event.target.value = '';
    },

    // Save current map to backend (create or update template)
    async saveToBackend() {
      if (!this.currentMap) return;

      this.isSaving = true;
      try {
        console.log('[TabletopEditor] Saving to backend...');

        const templateId = this.currentMap._templateId;

        if (templateId) {
          // Update existing template
          const updatedTemplate = await battleMapService.updateTemplate(
            templateId,
            this.currentMap,
            this.currentMap.backgroundImage
          );

          // Update template tracking
          this.currentMap._templateId = updatedTemplate.id;
          this.currentMap._templateVersion = updatedTemplate.template_version;

          console.log('[TabletopEditor] ✅ Map updated successfully:', updatedTemplate.id);
        } else {
          // Create new template
          const newTemplate = await battleMapService.createTemplate(
            this.currentMap,
            this.currentMap.backgroundImage
          );

          // Track template ID for future updates
          this.currentMap._templateId = newTemplate.id;
          this.currentMap._templateVersion = newTemplate.template_version;

          console.log('[TabletopEditor] ✅ Map saved to backend:', newTemplate.id);
        }

        // Mark as saved
        this.saveMap();
      } catch (error) {
        console.error('[TabletopEditor] ❌ Save error:', error.message);
      } finally {
        this.isSaving = false;
      }
    },

    // Export current map from backend as PNG
    async exportToDisk() {
      if (!this.currentMap || !this.currentMap._templateId) {
        console.warn('[TabletopEditor] Cannot export - please save the map to backend first');
        return;
      }

      try {
        console.log('[TabletopEditor] Exporting from backend...');
        await battleMapService.exportToDisk(this.currentMap._templateId);
        console.log('[TabletopEditor] ✅ Export complete');
      } catch (error) {
        console.error('[TabletopEditor] ❌ Export error:', error.message);
      }
    },

    // Load template from library
    async handleLoadFromLibrary(templateId) {
      try {
        console.log('[TabletopEditor] Loading template from library:', templateId);

        const mapData = await battleMapService.loadTemplate(templateId);

        // Load map in editor
        this.loadMap(mapData);

        // Background image is now fetched automatically by battleMapService.loadTemplate

        console.log('[TabletopEditor] ✅ Map loaded successfully:', mapData.metadata?.name, 'Template ID:', templateId);
      } catch (error) {
        console.error('[TabletopEditor] ❌ Load error:', error.message);
      }
    },

    // Test map in player mode
    testMap() {
      if (!this.currentMap || !this.currentMap._templateId) {
        console.warn('[TabletopEditor] Cannot test - please save the map to backend first');
        return;
      }

      // Navigate to player mode with template ID
      console.log('[TabletopEditor] Opening test mode for template:', this.currentMap._templateId);
      this.$router.push({
        name: 'TabletopMapPlayer',
        query: { templateId: this.currentMap._templateId }
      });
    },

    importBackground() {
      this.$refs.backgroundInput.click();
    },

    handleBackgroundFile(event) {
      const file = event.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const imageDataURL = e.target.result;
        this.setBackgroundImage(imageDataURL);

        // Load image to get dimensions and set as reference dimensions
        const img = new Image();
        img.onload = () => {
          // Update grid config with reference dimensions
          this.updateGridConfig({
            referenceImageWidth: img.width,
            referenceImageHeight: img.height
          });
          console.log('[Background] Set reference dimensions:', img.width, 'x', img.height);
        };
        img.src = imageDataURL;
      };
      reader.readAsDataURL(file);

      // Reset input
      event.target.value = '';
    },

    handleAutoMap() {
      if (!this.currentMap) return;

      const message =
        'Auto-map will automatically generate movement paths for all passable cells.\n\n' +
        '⚠️ WARNING: This will REPLACE all existing edges/connections!\n\n' +
        'The system will:\n' +
        '• Create edges between all adjacent passable cells\n' +
        '• Include both cardinal (N, S, E, W) and diagonal (NE, NW, SE, SW) connections\n' +
        '• Skip impassable cells (rock, water, mountain, etc.)\n' +
        '• Work on all active layers\n\n' +
        'Do you want to continue?';

      console.log('[Auto-map] Generating edges...');
      this.autoGenerateEdges();
      console.log('[Auto-map] ✅ Edge generation complete! Edges generated for all passable cells. Remember to save your map.');
    },

    async returnToCampaignBuilder() {
      if (!this.canReturnToCampaignBuilder || this.isReturningToCampaignBuilder) return;
      const {campaignId, positionId} = this.campaignBuilderReturn;
      this.isReturningToCampaignBuilder = true;
      this.campaignBuilderReturnError = null;
      try {
        await campaignBuilderService.assignTacticalMap(campaignId, {
          position_id: positionId,
          template_id: this.currentMap._templateId,
        });
        sessionStorage.removeItem(CAMPAIGN_BUILDER_RETURN_KEY);
        this.$router.push({name: 'CampaignBuilder', query: {campaign: campaignId}});
      } catch (error) {
        console.error('[TabletopEditor] Failed to hand the new map back to the campaign builder:', error.message);
        this.campaignBuilderReturnError = 'Could not hand this map back to the Campaign Builder. Check your connection and try again.';
      } finally {
        this.isReturningToCampaignBuilder = false;
      }
    }
  },
  beforeUnmount() {
    // Log unsaved changes warning
    if (this.isDirty) {
      console.warn('[TabletopEditor] Leaving with unsaved changes');
    }
  }
};
</script>

<style scoped>
.toolbar-section {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-lg);
}

.map-name {
  font-size: 0.8rem;
  color: var(--gm-text-muted);
  font-style: italic;
}

.campaign-builder-return-notice {
  margin: var(--gm-gap-sm) var(--gm-gap-lg) 0;
}

.campaign-builder-return-error {
  margin: var(--gm-gap-sm) var(--gm-gap-lg) 0;
}

/* Main Content */
.editor-content {
  gap: var(--gm-gap);
  padding: var(--gm-gap);
  overflow: hidden;
}

.left-panel,
.right-panel {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap);
  width: 17.5rem;
  flex-shrink: 0;
  padding-bottom: var(--gm-dock-clearance);
}

.center-panel {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.center-panel--split > * {
  flex: 1 1 0;
  min-width: 0;
}

.center-panel .center-panel__pane {
  align-self: flex-start;
  position: sticky;
  top: 0;
  height: calc(100vh - 8rem);
  border-left: 1px solid var(--gm-border-dim);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  width: 90%;
  max-width: 30rem;
}

.modal-title {
  margin: 0 0 var(--gm-gap-lg);
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap);
  margin-bottom: var(--gm-gap-lg);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--gm-gap-sm);
}
</style>
